/**
 * The Recent access card on the artifact detail screen (f15.6), mirroring the
 * "Recent access" panel of `artifact-detail.html`.
 *
 * A read-only, filtered view of the platform audit log: every view, download
 * and code attempt on this artifact. Rendered for everyone who can open the
 * detail screen. The prototype's Export button is intentionally absent
 * (Q-f15.6-3).
 */

import { History, RefreshCw, TriangleAlert } from 'lucide-react';

import type { ArtifactAccessEvent } from '@/api/client';
import { generalMessage } from '@/api/fieldErrors';
import { useArtifactAccessEvents } from '@/api/queries';

import { EVENT_BADGES, clientText, formatWhen, throughText, versionText } from './accessEventView';

function AccessEventRow({ event }: { event: ArtifactAccessEvent }) {
  const badge = EVENT_BADGES[event.kind];
  const through = throughText(event);
  const version = versionText(event);
  return (
    <tr>
      <td className="hint" title={event.occurred_at}>
        {formatWhen(event.occurred_at)}
      </td>
      <td>
        <span className={`badge ${badge.pill}`} title={event.reason ?? undefined}>
          {badge.text}
        </span>
      </td>
      {through ? <td>{through}</td> : <td className="hint">unknown code</td>}
      {version ? <td className="mono">{version}</td> : <td className="hint">&mdash;</td>}
      <td className="hint">{clientText(event)}</td>
    </tr>
  );
}

export function RecentAccessCard({ artifactId }: { artifactId: string }) {
  const history = useArtifactAccessEvents(artifactId);
  const events = history.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="card">
      <div className="card-head">
        <h3>Recent access</h3>
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          title="Refresh"
          aria-label="Refresh"
          disabled={history.isFetching}
          onClick={() => void history.refetch()}
        >
          <RefreshCw aria-hidden="true" />
        </button>
      </div>
      <div className="card-body access-codes-body">
        <p className="hint mb-4">
          Every view, download and code attempt, from the platform audit log. Codes are never recorded.
        </p>
        {history.isPending ? (
          <div aria-busy="true" aria-label="Loading access history">
            {[0, 1, 2].map((index) => (
              <span key={index} className="skeleton-bar group-skeleton-line" />
            ))}
          </div>
        ) : history.error ? (
          <div className="alert alert-danger" role="alert">
            <TriangleAlert aria-hidden="true" />
            <div>
              <div className="a-title">Access history could not be loaded</div>
              <div className="a-body">{generalMessage(history.error) ?? 'Please try again.'}</div>
            </div>
          </div>
        ) : events.length === 0 ? (
          <div className="empty">
            <div className="e-icon">
              <History aria-hidden="true" />
            </div>
            <h3>No access recorded yet</h3>
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table className="data compact">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Event</th>
                    <th>Through</th>
                    <th>Version</th>
                    <th>Client info</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <AccessEventRow key={event.id} event={event} />
                  ))}
                </tbody>
              </table>
            </div>
            {history.hasNextPage && (
              <button
                type="button"
                className="btn btn-secondary btn-sm mt-4"
                disabled={history.isFetchingNextPage}
                onClick={() => void history.fetchNextPage()}
              >
                {history.isFetchingNextPage ? 'Loading…' : 'Show more'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
